import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas, qaSchema } from "@/lib/seo";

const UPDATED = "2026-09-29";
const ANSWER =
  "Die meisten Shopify-Shops brauchen weniger Apps als sie haben. Sinnvoll sind meist nur: eine App für Bewertungen, eine für E-Mail-Marketing und je nach Bedarf eine für Rechnungen oder Übersetzung. Jede zusätzliche App kostet Geld und kann den Shop langsamer machen.";

const SITE = "https://shopify-freelancer.net";
const PATH = "/fragen/welche-shopify-apps-brauche-ich";
const TITLE = "Welche Shopify Apps brauche ich wirklich?";
const DESCRIPTION =
  "Welche Shopify Apps sich wirklich lohnen, welche Sie weglassen können und warum jede zusätzliche App den Shop ausbremst.";

const faqs = [
  {
    q: "Welche Apps sind fast immer sinnvoll?",
    a: "Bewertungen, E-Mail-Marketing und bei Bedarf Rechnungen oder Übersetzung. Das deckt die meisten Shops ab.",
  },
  {
    q: "Warum sind zu viele Apps ein Problem?",
    a: "Jede App lädt eigenen Code in Ihren Shop. Das macht ihn langsamer, und langsame Shops verkaufen schlechter. Dazu kommen die monatlichen Gebühren.",
  },
  {
    q: "Wie finde ich überflüssige Apps?",
    a: "Prüfen Sie jede App: Nutze ich sie wirklich? Bringt sie messbar Umsatz? Was sie im Alltag nicht leistet, raus damit. Deinstallierte Apps hinterlassen oft Reste im Code, die man entfernen sollte.",
  },
  {
    q: "App oder eigene Entwicklung?",
    a: "Bei Standardaufgaben ist eine gute App günstiger. Bei etwas, das genau zu Ihrem Ablauf passen soll, ist eine eigene Lösung oft besser und ohne Monatsgebühr.",
  },
];

export const Route = createFileRoute("/fragen/welche-shopify-apps-brauche-ich")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      ...articleSchemas({
        title: TITLE,
        description: DESCRIPTION,
        path: PATH,
        faqs,
        section: "Shopify Apps",
        updated: UPDATED,
        breadcrumb: { name: "Fragen", path: "/fragen" },
      }),
      qaSchema({
        question: "Welche Shopify Apps brauche ich?",
        answer: ANSWER,
        path: PATH,
        updated: UPDATED,
      }),
    ],
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Frage · Apps"
      title="Welche Shopify Apps brauche ich wirklich?"
      intro="Kurz gesagt: weniger als Sie denken. Bewertungen, E-Mail und je nach Bedarf Rechnungen oder Übersetzung reichen den meisten Shops."
      source="frage-shopify-apps"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/fragen", label: "Fragen" }}
      sections={[
        {
          h: "Die sinnvolle Grundausstattung",
          p: [],
          list: [
            "Bewertungen: schafft Vertrauen und hilft beim Verkaufen",
            "E-Mail-Marketing: bringt Käufer zurück, ohne neue Werbekosten",
            "Rechnungen oder Übersetzung: nur wenn Ihr Geschäft es braucht",
          ],
        },
        {
          h: "Der versteckte Preis zu vieler Apps",
          p: [
            "Jede App lädt eigenen Code und kostet monatlich. Zehn Apps à 20 € sind 2.400 € im Jahr, und der Shop wird dabei langsamer. Langsame Shops verlieren Käufer.",
          ],
        },
        {
          h: "Aufräumen lohnt sich",
          p: [
            "Eine App-Prüfung zeigt, welche Apps wirklich Umsatz bringen und welche nur kosten. Beim Entfernen sollten auch die Code-Reste im Theme gelöscht werden, sonst bleibt der Shop langsam.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-theme-anpassung", label: "Theme-Anpassung" },
        { to: "/shopify-conversion-optimierung", label: "Conversion-Optimierung" },
        { to: "/fragen/was-kostet-ein-shopify-shop", label: "Was kostet ein Shopify-Shop?" },
      ]}
    />
  );
}
